import * as logger from 'loglevel';
import { getUuid } from '@front10/helpers/dist/uuid';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';
import transformRequestPopularDestination from './transformRequest/transformPopularDestinations';
import axiosWrapper from '../../helpers/axiosWrapper';
import { yearAdjustmentPatch } from './yearAdjustmentPatch';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

/**
 * The group-routes API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

const transformData = (data, filter) => {
  let showCityName = true;
  let colWidth = '4';
  let columnNumbers = null;
  if (data.length === 1) {
    colWidth = '12';
    columnNumbers = 3;
    showCityName = false;
  } else if (data.length === 2) {
    colWidth = '6';
  }
  const response = {
    showCityName,
    columnNumbers,
    colWidth,
    image: '',
    data
  };
  const { uiDatesYearsAdjustmentPatch } = filter;
  if (data.length > 0) {
    let equals = true;
    let { name: cityName } = data[0];
    const newData = data.map((destination) => {
      const newDestination = { ...destination };
      if (destination.name !== cityName) {
        equals = false;
      }
      cityName = destination.name;
      if (data.length === 1) {
        if (destination.fares.length === 1) {
          response.columnNumbers = 1;
        }
        if (destination.fares.length === 2) {
          response.columnNumbers = 2;
          response.oneColum = false;
        }
      }
      const newFares = destination.fares.map((fare) => {
        let { searchDate } = fare;
        // For date in safary y ie
        if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
          searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
            searchDate.length - 2
          )}`;
        }

        const newFare = Object.assign({}, fare, { searchDate });
        if (newFare.redemption && newFare.formattedTotalPrice) {
          newFare.redemption.formattedAmount = newFare.formattedTotalPrice;
        }
        newFare.journeyType = journeyTypeMap[fare.flightType];
        newFare.index = getUuid();
        yearAdjustmentPatch(newFare, uiDatesYearsAdjustmentPatch);
        return newFare;
      });
      newDestination.fares = newFares;
      newDestination.id = getUuid();
      return newDestination;
    });
    response.data = newData;
    if (equals) {
      response.image = data[0].image;
      response.label = data[0].name;
      response.labelSecondary = data[0].iataCode;
      response.showDefaultImage = !data[0].image;
    }
  }
  return response;
};

const validatePopularResponse = (populars) => {
  const validation = { isValid: true, message: '' };
  if (!populars || !Array.isArray(populars)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: populars,
      message: 'INTEGRATION__POPULAR__DESTINATION: Populars destinations must be an Array'
    };
  }
  if (populars.length > 0) {
    const popularItem = populars[0];
    if (!popularItem.iataCode || !popularItem.name || !popularItem.fares) {
      return {
        isValid: false,
        data: popularItem,
        message:
          'INTEGRATION__POPULAR__DESTINATION: PopularItem SCHEMA is invalid, please check Fares API documentation.'
      };
    }
  }
  return validation;
};

const getPopularDestinations = (filter, integrationSettings, _, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__POPULAR_DESTINATION: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v2PopularDestinations: popularDestinationsApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = popularDestinationsApiSettings;

    const filterCopy = { ...filter };
    if (
      !popularDestinationsApiSettings ||
      !popularDestinationsApiSettings.method ||
      !popularDestinationsApiSettings.endPoint ||
      !popularDestinationsApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__POPULAR__DESTINATION: **integrationSettings.v2PopularDestinations** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const { siteEdition, languageCode, origins, destinations } = filter;

    if (!siteEdition) {
      const MESSAGE = `INTEGRATION__POPULAR__DESTINATION: **siteEdition** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    filterCopy.siteEdition = siteEdition;

    if (!languageCode) {
      const MESSAGE = `INTEGRATION__POPULAR__DESTINATION: **language** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    // filterCopy.languageCode = language;

    // Conver EM start and end number of days to dates
    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__POPULAR__DESTINATION'
    );

    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__POPULAR__DESTINATION'
      );
    }

    filterCopy.origins = [];
    if (origins && Array.isArray(origins)) {
      filterCopy.origins = origins;
    }

    filterCopy.destinations = [];
    if (destinations && Array.isArray(destinations)) {
      filterCopy.destinations = destinations;
    }
    axiosWrapper(
      {
        method: popularDestinationsApiSettings.method,
        url: `${popularDestinationsApiSettings.endPoint}`,
        headers: { ...popularDestinationsApiSettings.headers },
        data: transformRequestPopularDestination(filterCopy)
      },
      {
        resource: 'INTEGRATION__POPULAR__DESTINATION',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validatePopularResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__POPULAR__DESTINATION: Successful request', response.data);
          const popularDestinations = transformData(response.data, filter);
          resolve(popularDestinations);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getPopularDestinations, validatePopularResponse };
